import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MapPin, Navigation, X } from 'lucide-react';
import { env } from '@/shared/config/env';
import {
  fetchPlacePredictions,
  prefetchGooglePlaces,
  resolvePlaceDetails,
  type PlacePrediction,
} from '@/shared/lib/maps/placesAutocomplete';
import { MinimalPlaceMap } from '@/shared/lib/maps/MinimalPlaceMap';
import {
  NEARBY_DEFAULT_RADIUS,
  NEARBY_RADIUS_OPTIONS,
} from '@/features/billboards/utils/nearbyBillboardsUtils';

export type LocalityPlace = {
  label: string;
  lat: number | null;
  lng: number | null;
  radius: number;
};

type LocalityRadiusFieldProps = {
  value: LocalityPlace;
  city?: string;
  onChange: (next: LocalityPlace) => void;
  /** `sheet` = compact Apple-style for filter modals (no duplicate hints). */
  variant?: 'default' | 'sheet';
};

const PRIMARY_RADII = [1, 2, 3, 5, 10, 15, 25, 50] as const;
const MIN_QUERY_CHARS = 2;
const DEBOUNCE_MS = 320;

type MenuStyle = CSSProperties;

function buildMenuStyle(trigger: HTMLElement): MenuStyle {
  const rect = trigger.getBoundingClientRect();
  const gap = 6;
  const pad = 12;
  const width = Math.min(
    Math.max(rect.width, 280),
    window.innerWidth - pad * 2,
  );
  const left = Math.min(
    Math.max(pad, rect.left),
    window.innerWidth - width - pad,
  );
  const spaceBelow = window.innerHeight - rect.bottom - gap - pad;
  const maxHeight = Math.min(280, Math.max(140, spaceBelow));

  return {
    position: 'fixed',
    top: rect.bottom + gap,
    left,
    width,
    maxHeight,
    zIndex: 10060,
  };
}

/**
 * Place search via paid Google Places (Autocomplete + Details session tokens)
 * + radius control for nearby screen filters.
 */
export function LocalityRadiusField({
  value,
  city,
  onChange,
  variant = 'default',
}: LocalityRadiusFieldProps) {
  const apiKey = env.GOOGLE_LOCATION_KEY;
  const country = env.COUNTRY === 'UAE' ? 'ae' : 'in';
  const listId = useId();
  const isSheet = variant === 'sheet';

  const [query, setQuery] = useState(value.label || '');
  const [open, setOpen] = useState(false);
  const [loadingMaps, setLoadingMaps] = useState(false);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [resolvingPlace, setResolvingPlace] = useState(false);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const [menuStyle, setMenuStyle] = useState<MenuStyle | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const hasPin =
    Number.isFinite(value.lat) &&
    Number.isFinite(value.lng) &&
    Boolean(value.label);

  // Keep query label in sync when parent clears / applies draft
  useEffect(() => {
    if (hasPin) {
      setQuery(value.label);
      setPredictions([]);
      setOpen(false);
      return;
    }
    if (!value.label) setQuery('');
  }, [hasPin, value.label]);

  // Prefetch Maps/Places when field mounts (no prediction charge)
  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    setLoadingMaps(true);
    prefetchGooglePlaces(apiKey);
    void import('@/shared/lib/maps/googleMaps')
      .then(({ loadGoogleMapsScript }) => loadGoogleMapsScript(apiKey))
      .then(() => {
        if (!cancelled) setMapsError(null);
      })
      .catch(() => {
        if (!cancelled) {
          setMapsError('Maps unavailable — type an area name instead.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMaps(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Debounced predictions (session-token AutocompleteService)
  useEffect(() => {
    if (!apiKey || hasPin) return;

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_CHARS) {
      setPredictions([]);
      setLoadingPredictions(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoadingPredictions(true);
      void fetchPlacePredictions({
        apiKey,
        input: trimmed,
        country,
        city,
        signal: controller.signal,
      })
        .then((items) => {
          if (controller.signal.aborted) return;
          setPredictions(items);
          setActiveIndex(items.length ? 0 : -1);
          setOpen(true);
          setMapsError(null);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setPredictions([]);
          setMapsError('Could not fetch place suggestions.');
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingPredictions(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [apiKey, query, country, city, hasPin]);

  const syncMenu = useCallback(() => {
    if (!inputRef.current) return;
    setMenuStyle(buildMenuStyle(inputRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open || hasPin) {
      setMenuStyle(null);
      return;
    }
    syncMenu();
  }, [open, hasPin, predictions.length, syncMenu]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => syncMenu();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, syncMenu]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selectPrediction = async (prediction: PlacePrediction) => {
    if (!apiKey) return;
    setResolvingPlace(true);
    setOpen(false);
    try {
      const place = await resolvePlaceDetails({
        apiKey,
        placeId: prediction.placeId,
      });
      if (!place) {
        setMapsError('Could not resolve that place.');
        return;
      }
      setQuery(place.label);
      onChangeRef.current({
        label: place.label,
        lat: place.lat,
        lng: place.lng,
        radius: value.radius || NEARBY_DEFAULT_RADIUS,
      });
      setPredictions([]);
      setMapsError(null);
    } catch {
      setMapsError('Could not resolve that place.');
    } finally {
      setResolvingPlace(false);
    }
  };

  const clearPlace = () => {
    setQuery('');
    setPredictions([]);
    setOpen(false);
    setActiveIndex(-1);
    onChange({
      label: '',
      lat: null,
      lng: null,
      radius: value.radius || NEARBY_DEFAULT_RADIUS,
    });
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const onInputChange = (next: string) => {
    setQuery(next);
    // Clear coordinates until a Google suggestion is chosen
    onChange({
      ...value,
      label: next,
      lat: null,
      lng: null,
    });
  };

  const radiusOptions = isSheet
    ? PRIMARY_RADII.filter((r) =>
        (NEARBY_RADIUS_OPTIONS as readonly number[]).includes(r),
      )
    : NEARBY_RADIUS_OPTIONS;

  const showMenu =
    open &&
    !hasPin &&
    Boolean(apiKey) &&
    menuStyle &&
    (loadingPredictions || predictions.length > 0 || query.trim().length >= MIN_QUERY_CHARS);

  const menu =
    showMenu && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-label="Place suggestions"
            style={menuStyle}
            className="flex flex-col overflow-hidden rounded-[14px] border border-black/[0.08] bg-white shadow-[0_18px_48px_-16px_rgba(16,38,73,0.42)]"
          >
            {loadingPredictions && predictions.length === 0 ? (
              <div className="flex items-center gap-2 px-3 py-3 text-[12px] text-[var(--dash-muted)]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching places…
              </div>
            ) : predictions.length === 0 ? (
              <p className="m-0 px-3 py-3 text-[12px] text-[var(--dash-muted)]">
                No places found — try a different spelling
              </p>
            ) : (
              <ul className="m-0 max-h-[inherit] list-none overflow-y-auto overscroll-contain p-1.5">
                {predictions.map((p, index) => {
                  const active = index === activeIndex;
                  return (
                    <li key={p.placeId} className="m-0 p-0">
                      <button
                        id={`${listId}-opt-${index}`}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => void selectPrediction(p)}
                        className={`flex w-full items-start gap-2.5 rounded-[10px] px-2.5 py-2.5 text-left transition ${
                          active
                            ? 'bg-[var(--dash-brand)]/[0.08]'
                            : 'hover:bg-black/[0.035]'
                        }`}
                      >
                        <MapPin
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--dash-brand)]"
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-semibold tracking-[-0.01em] text-[var(--dash-ink)]">
                            {p.primaryText}
                          </span>
                          {p.secondaryText ? (
                            <span className="mt-0.5 block text-[11px] leading-snug text-[var(--dash-muted)]">
                              {p.secondaryText}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="m-0 border-t border-black/[0.05] bg-[#FAFBFC] px-3 py-1.5 text-[10px] text-[var(--dash-muted)]">
              Powered by Google · select a suggestion to set coordinates
            </p>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={`text-left ${isSheet ? 'space-y-3' : 'space-y-1.5'}`}
    >
      {hasPin ? (
        <div className="space-y-2.5">
          <div
            className={`flex items-start gap-2.5 rounded-[14px] border border-black/[0.06] bg-white p-2.5 ${
              isSheet ? '' : 'sm:p-3'
            }`}
          >
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--dash-brand)]/10 text-[var(--dash-brand)]">
              {resolvingPlace ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Navigation className="h-3.5 w-3.5" aria-hidden />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--dash-muted)]">
                Selected place
              </p>
              <p
                className="m-0 mt-0.5 text-[13px] font-semibold leading-snug tracking-[-0.01em] text-[var(--dash-ink)]"
                title={value.label}
              >
                {value.label}
              </p>
            </div>
            <button
              type="button"
              onClick={clearPlace}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[var(--dash-muted)] transition hover:bg-black/[0.05] hover:text-[var(--dash-ink)]"
              aria-label="Clear locality"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {apiKey ? (
            <MinimalPlaceMap
              lat={value.lat as number}
              lng={value.lng as number}
              radiusKm={value.radius || NEARBY_DEFAULT_RADIUS}
              title={value.label}
              height={isSheet ? 152 : 168}
            />
          ) : null}
        </div>
      ) : (
        <div className="relative">
          <MapPin
            className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--dash-brand)]"
            aria-hidden
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={Boolean(showMenu)}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              activeIndex >= 0 && predictions[activeIndex]
                ? `${listId}-opt-${activeIndex}`
                : undefined
            }
            disabled={resolvingPlace}
            onFocus={() => {
              if (predictions.length || query.trim().length >= MIN_QUERY_CHARS) {
                setOpen(true);
              }
            }}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (!predictions.length) return;
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setOpen(true);
                setActiveIndex((i) =>
                  i < predictions.length - 1 ? i + 1 : 0,
                );
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex((i) =>
                  i <= 0 ? predictions.length - 1 : i - 1,
                );
              } else if (e.key === 'Enter' && activeIndex >= 0) {
                e.preventDefault();
                const pick = predictions[activeIndex];
                if (pick) void selectPrediction(pick);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder={
              city ? `Search places in ${city}…` : 'Search a locality or landmark…'
            }
            className="h-11 w-full rounded-[14px] border border-black/[0.08] bg-white py-2 pr-10 pl-10 text-[13px] text-[var(--dash-ink)] outline-none transition placeholder:text-[#8E98A8] focus:border-[var(--dash-brand)]/40 focus:shadow-[0_0_0_3px_rgba(41,121,255,0.12)] disabled:opacity-60"
            aria-label="Search locality"
          />
          <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
            {loadingMaps || loadingPredictions || resolvingPlace ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--dash-muted)]" />
            ) : query ? (
              <button
                type="button"
                className="pointer-events-auto inline-flex rounded-full p-0.5 text-[var(--dash-muted)] hover:bg-black/[0.05] hover:text-[var(--dash-ink)]"
                aria-label="Clear search"
                onClick={clearPlace}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </span>
          {menu}
        </div>
      )}

      {mapsError ? (
        <p className="m-0 text-[11px] font-medium text-amber-700" role="status">
          {mapsError}
        </p>
      ) : null}

      {!apiKey && !isSheet ? (
        <p className="m-0 text-xs text-[var(--dash-muted)]">
          Type a locality name to filter screens (Maps key not configured).
        </p>
      ) : null}

      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <p className="m-0 text-[11px] font-semibold text-[var(--dash-muted)]">
            Search radius
          </p>
          {!hasPin ? (
            <p className="m-0 text-[10px] text-[var(--dash-muted)]">
              Select a Google suggestion first
            </p>
          ) : (
            <p className="m-0 text-[11px] font-semibold tabular-nums text-[var(--dash-ink)]">
              {value.radius || NEARBY_DEFAULT_RADIUS} km
            </p>
          )}
        </div>
        <div
          className={`flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-none ${
            !hasPin ? 'pointer-events-none opacity-40' : ''
          }`}
          role="radiogroup"
          aria-label="Search radius"
          aria-disabled={!hasPin}
        >
          {radiusOptions.map((radius) => {
            const active = (value.radius || NEARBY_DEFAULT_RADIUS) === radius;
            return (
              <button
                key={radius}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={!hasPin}
                onClick={() => onChange({ ...value, radius })}
                className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold tabular-nums transition active:scale-[0.97] ${
                  active
                    ? 'bg-[var(--dash-brand)] text-white shadow-[0_2px_8px_-2px_rgba(41,121,255,0.5)]'
                    : 'bg-[#EEF1F5] text-[var(--dash-ink)] hover:bg-[#E4E8EF]'
                }`}
              >
                {radius} km
              </button>
            );
          })}
        </div>
      </div>

      {!isSheet && apiKey ? (
        <p className="m-0 text-xs text-[var(--dash-muted)]">
          Choose a Google suggestion to lock coordinates, then set radius.
        </p>
      ) : null}
    </div>
  );
}
